import { consumeStream } from '../common/streaming.js';
import { adaptBackendEvent } from '../common/protocol-adapter.js';
import { buildChatEndpointUrl } from '../common/api-paths.js';
import { discoverInjectorAdapter } from '../common/customization-factories.js';
import { normalizeFindSimilarPayload } from '../common/find-similar-payload.js';
import { cachedFetch } from '../common/request-response-cache.js';
import type { StreamEvent } from '../common/types.js';
import type { ChatTransportConfig } from '../common/api-paths.js';
import type { ActionEnrichmentContext, ProcessActionRequest, StreamCallbacks } from '../common/transport.js';
export type {
  ActionEnrichmentContext,
  BackendRequestMeta,
  ProcessActionRequest,
  StreamCallbacks,
} from '../common/transport.js';

/**
 * Enriches action payloads with fields the backend expects.
 * Only adds fields that are not already present in the payload.
 */
export function enrichActionPayload(
  action: { title: string; type: string; payload?: unknown },
  ctx: ActionEnrichmentContext,
): { title: string; type: string; payload?: unknown } {
  const type = action.type;
  const existing =
    action.payload != null && typeof action.payload === 'object' && !Array.isArray(action.payload)
      ? (action.payload as Record<string, unknown>)
      : {};

  // Helper: only set if not already present
  const merge = (additions: Record<string, unknown>): Record<string, unknown> => {
    const result = { ...existing };
    for (const [key, value] of Object.entries(additions)) {
      if (!(key in result)) {
        result[key] = value;
      }
    }
    return result;
  };

  switch (type) {
    case 'user_message':
    case 'inputText': {
      const isPlainStringPayload = typeof action.payload === 'string';
      const baseExisting = isPlainStringPayload
        ? { text: action.payload }
        : action.payload != null && typeof action.payload === 'object' && !Array.isArray(action.payload)
          ? (action.payload as Record<string, unknown>)
          : existing;
      const suppressNonPdpHostContext =
        ctx.genericNonPdpLaunch === true &&
        ctx.pageContext?.pageType !== 'pdp' &&
        (baseExisting['is_entry_question'] === 1 || baseExisting['is_entry_question'] === true);
      const suppressSuggestedSearchSkuContext =
        baseExisting['is_search_suggestion'] === 1 || baseExisting['is_search_suggestion'] === true;
      const additions: Record<string, unknown> = {
        is_launcher: 0,
      };
      if (!suppressNonPdpHostContext && ctx.pageContext?.extra) additions['page_details'] = ctx.pageContext.extra;
      if (
        !suppressNonPdpHostContext &&
        !suppressSuggestedSearchSkuContext &&
        !isPlainStringPayload &&
        !('sku_list' in baseExisting)
      ) {
        const skuList =
          ctx.pageContext?.skuList && ctx.pageContext.skuList.length > 0
            ? ctx.pageContext.skuList
            : ctx.pageContext?.sku
              ? [ctx.pageContext.sku]
              : undefined;
        if (skuList && skuList.length > 0) additions['sku_list'] = skuList;
      }
      // is_suggested_text may already be set by adapter — don't overwrite
      if (!('is_suggested_text' in baseExisting)) additions['is_suggested_text'] = 0;
      const result = { ...baseExisting };
      for (const [key, value] of Object.entries(additions)) {
        if (!(key in result)) {
          result[key] = value;
        }
      }
      return { ...action, payload: result };
    }

    case 'findSimilar': {
      const additions: Record<string, unknown> = {
        is_launcher: 0,
      };
      if (action.title) {
        additions['text'] = action.title;
        additions['input'] = action.title;
      }
      return { ...action, payload: normalizeFindSimilarPayload(merge(additions)) };
    }

    case 'getComparisonTable': {
      const existing = action.payload;
      if (
        existing &&
        typeof existing === 'object' &&
        !Array.isArray(existing) &&
        'gengage_analytics_source' in existing
      ) {
        const next = { ...(existing as Record<string, unknown>) };
        delete next['gengage_analytics_source'];
        return { ...action, payload: next };
      }
      return action;
    }

    case 'launchProductList': {
      // sku_list must be provided by the caller in the payload; no additional enrichment
      return action;
    }

    case 'launchHomepage': {
      return action;
    }

    case 'addToCart': {
      const additions: Record<string, unknown> = {};
      if (!('error_message' in existing)) additions['error_message'] = '';
      return { ...action, payload: merge(additions) };
    }

    case 'reviewSummary': {
      const additions: Record<string, unknown> = {};
      if (ctx.pageContext?.sku && !('sku' in existing)) {
        additions['sku'] = ctx.pageContext.sku;
      }
      if (Object.keys(additions).length === 0) return action;
      return { ...action, payload: merge(additions) };
    }

    default:
      return action;
  }
}

/**
 * Action type mapping.
 * The backend's ActionType enum uses `inputText` for user messages,
 * while the frontend uses `user_message`. Map at the boundary.
 */
const ACTION_TYPE_MAP: Record<string, string> = {
  user_message: 'inputText',
};

/**
 * Builds the request body for `/chat/process_action`.
 *
 * Backend expects `type` and `payload` at the top level —
 * matching chat_api.py's current schema.
 */
function buildRequestBody(request: ProcessActionRequest): string {
  const { action, type: flatType, payload: flatPayload, ...rest } = request;
  // Prefer top-level type/payload; fall back to deprecated action wrapper.
  const rawType = flatType ?? action?.type ?? 'inputText';
  const rawPayload = flatPayload ?? action?.payload;
  const mappedType = ACTION_TYPE_MAP[rawType] ?? rawType;
  const body: Record<string, unknown> = {
    ...rest,
    type: mappedType,
  };
  if (rawPayload !== undefined) {
    // Backend expects payload as an object. Wrap string payloads for compatibility.
    body.payload = typeof rawPayload === 'string' ? { text: rawPayload } : rawPayload;
  }
  return JSON.stringify(body);
}

function normalizeProcessActionRequest(request: ProcessActionRequest): ProcessActionRequest {
  // AGENTIC_ADAPTER_SEAM: adapters receive the same flattened payload shape as `/chat/process_action`.
  return JSON.parse(buildRequestBody(request)) as ProcessActionRequest;
}

function isPromiseLike(value: unknown): value is PromiseLike<void> {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

function createTerminalGuardedCallbacks(
  callbacks: StreamCallbacks,
  signal?: AbortSignal,
): {
  callbacks: StreamCallbacks;
  fireDone: () => void;
} {
  // AGENTIC_ADAPTER_SEAM: mirror consumeStream terminal behavior for adapter-driven turns.
  let doneFired = false;
  const isAborted = () => signal?.aborted === true;
  const isClosed = () => doneFired || isAborted();
  const fireDone = () => {
    if (isClosed()) return;
    doneFired = true;
    callbacks.onDone();
  };
  return {
    fireDone,
    callbacks: {
      onTextChunk: (...args) => {
        if (isClosed()) return;
        callbacks.onTextChunk(...args);
      },
      onUISpec: (...args) => {
        if (isClosed()) return;
        callbacks.onUISpec(...args);
      },
      onAction: (event) => {
        if (isClosed()) return;
        callbacks.onAction(event);
      },
      onMetadata: (event) => {
        if (isClosed()) return;
        callbacks.onMetadata(event);
      },
      onError: (err) => {
        if (isAborted()) return;
        callbacks.onError(err);
      },
      onDone: fireDone,
    },
  };
}

export function sendChatMessage(
  request: ProcessActionRequest,
  callbacks: StreamCallbacks,
  transport: ChatTransportConfig,
): AbortController {
  const controller = new AbortController();
  // AGENTIC_ADAPTER_SEAM: path selection is presence-based only; no account flags or URL flags here.
  const injectorAdapter = transport.injectorAdapter ?? discoverInjectorAdapter(transport.accountId);

  if (injectorAdapter) {
    void (async () => {
      const guarded = createTerminalGuardedCallbacks(callbacks, controller.signal);
      try {
        // AGENTIC_ADAPTER_SEAM: defer adapter start so immediate aborts win, matching fetch cleanup.
        await Promise.resolve();
        if (controller.signal.aborted) return;
        const completion = injectorAdapter(
          normalizeProcessActionRequest(request),
          guarded.callbacks,
          controller.signal,
          transport.attachment,
        );
        if (isPromiseLike(completion)) {
          await completion;
          guarded.fireDone();
        }
      } catch (err) {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        guarded.callbacks.onError(err instanceof Error ? err : new Error(String(err)));
      }
    })();
    return controller;
  }

  const run = async (): Promise<void> => {
    try {
      const url = buildChatEndpointUrl('process_action', transport);
      const requestBody = buildRequestBody(request);

      // Use FormData only when an attachment is present; otherwise send JSON.
      const useFormData = transport.attachment !== undefined;

      let fetchInit: RequestInit;
      if (useFormData) {
        const formData = new FormData();
        formData.append('request', requestBody);
        if (transport.attachment !== undefined) {
          formData.append('attachment', transport.attachment);
        }
        fetchInit = {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        };
      } else {
        fetchInit = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: requestBody,
          signal: controller.signal,
        };
      }

      const response = useFormData ? await fetch(url, fetchInit) : await cachedFetch(url, fetchInit);

      if (!response.ok) {
        let detail = response.statusText;
        try {
          const body = await response.json();
          const msg =
            (body as Record<string, unknown>).detail ??
            (body as Record<string, unknown>).message ??
            (body as Record<string, unknown>).error;
          if (typeof msg === 'string') detail = msg;
        } catch {
          /* body not JSON — keep statusText */
        }
        callbacks.onError(new Error(`HTTP ${response.status}: ${detail}`));
        return;
      }

      const guarded = createTerminalGuardedCallbacks(callbacks, controller.signal);

      await consumeStream(response, {
        onEvent: (event: StreamEvent) => {
          const normalized = adaptBackendEvent(event as unknown as Record<string, unknown>);

          if (!normalized) return;

          // Late product_list events (for example, slow LLM rank landing after
          // the backend closed the turn) must not mutate finalized UI.
          switch (normalized.type) {
            case 'text_chunk':
              guarded.callbacks.onTextChunk(normalized.content, normalized.final === true, {
                productMentions: normalized.productMentions,
                skuToProductItem: normalized.skuToProductItem,
                conversationMode: normalized.conversationMode,
                renderHint: normalized.renderHint,
                kvkk: normalized.kvkk,
              });
              break;
            case 'ui_spec':
              guarded.callbacks.onUISpec(
                normalized.spec,
                normalized.widget,
                normalized.panelHint,
                normalized.clearPanel === true,
              );
              break;
            case 'action':
              guarded.callbacks.onAction(normalized);
              break;
            case 'metadata':
              guarded.callbacks.onMetadata(normalized);
              break;
            case 'error':
              guarded.callbacks.onError(new Error(normalized.message));
              break;
            case 'done':
              guarded.fireDone();
              break;
          }
        },
        onError: guarded.callbacks.onError,
        signal: controller.signal,
      });

      // Fallback: if stream completed without a chatStreamEnd event (backend
      // bug or truncated response), ensure onDone fires so the widget doesn't
      // get stuck with a spinning typing indicator.
      guarded.fireDone();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  void run();
  return controller;
}
